import { ref, reactive, watch, onMounted } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { useRegisterStore } from '@/stores/register-store'
import { useLoginStore } from '@/stores/login-store'

export function useCreatePinForm() {
  const router = useRouter()
  const route = useRoute()
  const registerStore = useRegisterStore()
  const loginStore = useLoginStore()

  const ruleFormRef = ref()
  const loading = reactive({ form: false })
  const pinLength = 6
  const pin = ref<string[]>(Array(pinLength).fill(''))
  const form = reactive({ pin: '' })
  const inputRefs = ref<(HTMLInputElement | null)[]>([])

  const setInputRef = (el: HTMLInputElement | null, index: number) => {
    if (el) inputRefs.value[index] = el
  }

  const onInput = (event: Event, index: number) => {
    const target = event.target as HTMLInputElement
    let val = target.value.replace(/[^0-9]/g, '')
    if (val.length > 1) val = val.slice(-1)
    pin.value[index] = val
    target.value = val
    if (val && index < pinLength - 1) {
      inputRefs.value[index + 1]?.focus()
    }
    form.pin = pin.value.join('')
  }

  const onKeydown = (event: KeyboardEvent, index: number) => {
    const target = event.target as HTMLInputElement
    if (event.key === 'Backspace' && !target.value && index > 0) {
      inputRefs.value[index - 1]?.focus()
    }
  }

  watch(pin, () => {
    form.pin = pin.value.join('')
  })

  const rules = {
    pin: [
      { required: true, message: 'PIN wajib diisi', trigger: 'blur' },
      { min: 6, max: 6, message: 'PIN harus 6 digit', trigger: 'blur' },
      {
        pattern: /^\d{6}$/,
        message: 'PIN harus berupa 6 digit angka',
        trigger: 'blur'
      }
    ]
  }

  const handleSubmit = async (formEl: any) => {
    if (!formEl) return
    form.pin = pin.value.join('')
    if (pin.value.some((d) => d === '' || d.length !== 1)) {
      const firstEmpty = pin.value.findIndex((d) => d === '' || d.length !== 1)
      if (firstEmpty !== -1) inputRefs.value[firstEmpty]?.focus()
      return
    }
    await formEl.validate((valid: boolean) => {
      if (valid) {
        registerStore.setRegisterData({ pin: form.pin })
        loginStore.setLoginData({ pin: form.pin })

        // Navigate to CreateAgainPinPage with query params if available
        if (route.query.flow === 'register' && route.query.phone) {
          router.push({
            name: 'CreateAgainPinPage',
            query: {
              phone: route.query.phone,
              countryCode: route.query.countryCode,
              fullname: route.query.fullname,
              ...(route.query.email && { email: route.query.email }),
              flow: 'register'
            }
          })
        } else {
          router.push({ name: 'CreateAgainPinPage' })
        }
      }
    })
  }

  onMounted(() => {
    inputRefs.value = inputRefs.value
  })

  return {
    form,
    rules,
    ruleFormRef,
    loading,
    pinLength,
    pin,
    inputRefs,
    setInputRef,
    onInput,
    onKeydown,
    handleSubmit
  }
}
